import { HearingPhaseBarDirective } from "../hearing-detail/hearing-phase-bar.directive";
import { CommonModule } from "@angular/common";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { EzDialogComponent } from "@template-ui/components/ez-dialog/ez-dialog.component";
import { EzDialogData } from "@template-ui/types";
import { UserService } from "@app/shared/services/user.service";
import { Component, DestroyRef, EventEmitter, HostListener, Input, Output, computed, effect, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject, buffer, of } from "rxjs";
import { catchError, concatMap, debounceTime, filter, finalize, map } from "rxjs/operators";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormInputType, InputChangeEvent, InputConfig } from "@ez-library/types/ez-form.types";

import { TabConfig } from "@template-ui/types";

import { RoleService } from "@app/shared/services/roles.service";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { WorkflowDraftConfirmationService } from "@app/platform-admin/workflow-registry-diagnostics/workflow-draft-confirmation.service";
import { formatPersonNameLastFirst } from "@app/shared/util/person-name";
import { PHMS_HEARING_TERMINOLOGY } from "../phms-hearing.terminology";
import { PHMS_DOCUMENT_TERMINOLOGY } from "../phms-document.terminology";
import { PhmsCaseDocumentsComponent } from "../documents/phms-case-documents.component";

import { HearingCycleContextStore } from "../hearing-detail/hearing-cycle-context.store";
import { PreHearingAnalysisNotesSectionComponent } from "./sections/pre-hearing-analysis-notes-section.component";
import { HearingTaskWorkspaceComponent } from "./hearing-task-workspace.component";
import { PreHearingCaseReviewSectionComponent } from "./sections/pre-hearing-case-review-section.component";
import { PreHearingVictimNotificationSectionComponent } from "./sections/pre-hearing-victim-notification-section.component";
import { isParticipantNotificationRequirement } from "./hearing-requirements";
import { HearingRecordService } from "./hearing-record.service";
import {
  HearingRecordDto,
  PreHearingFormValues,
  ProceedingHearingCaptureDto,
  ProceedingHearingCaptureUpsertRequest,
} from "./hearing-record.models";

/**
 * Author permission for the S2 capture PUT (#PHMS-HREC). Until S6 introduces a dedicated
 * `phms.hearing_record.author`, the backend gates authoring on this existing proceeding-scoped
 * record-author permission; the surface mirrors that so the read-only state matches the server.
 */
const CAPTURE_AUTHOR_PERMISSION = "inmate_offense_record.author";

/** Debounce window for autosave; long enough to coalesce keystrokes, short enough to feel live. */
const AUTOSAVE_DEBOUNCE_MS = 800;

type PendingCaptureEdit = { proceedingId: number; revision: number; payload: ProceedingHearingCaptureUpsertRequest };
type PendingCaptureBatch = PendingCaptureEdit & { count: number };

/**
 * Pre-hearing capture surface (#PHMS-HREC S3).
 *
 * One phase of the hearing-record form: a coordinator enters the portion known *before* the hearing
 * — risk/CRA note, criminal & institutional-history confirmation — which autosaves
 * (debounced) to the S2 capture via {@link HearingRecordService.upsertCapture}. Read-only context
 * (name / custody) is shown from the cohesive S1 record. The surface is read-only when the actor lacks
 * the capture author permission, matching the server's PUT gate.
 *
 */
@Component({
  selector: "phms-pre-hearing-capture",
  standalone: true,
  // Tab bodies (history and risk, private notes, SDMF, attendees, documents) are instantiated
  // dynamically through the TabConfig, so they are imported as classes, not template imports.
  imports: [HearingPhaseBarDirective, CommonModule, UiLibraryModule, HearingTaskWorkspaceComponent],
  templateUrl: "./pre-hearing-capture.component.html",
  styleUrl: "./pre-hearing-capture.component.scss",
})
export class PreHearingCaptureComponent {
  /** Adopted preparation pages use server workflow tasks; capture remains ordinary record editing. */
  @Input() workflowManaged = false;
  /** Compatibility record acknowledgements never claim workflow completion or advancement. */
  @Input() recordOnlyReview = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly roles = inject(RoleService);
  private readonly user = inject(UserService);
  private readonly dialog = inject(MatDialog);
  private readonly draftConfirmation = inject(WorkflowDraftConfirmationService);
  private navigationConfirmation: Promise<boolean> | null = null;
  private acknowledgementUnconfirmed = false;
  /** Navigation must retain failures for other fields after a later partial save succeeds. */
  private readonly unconfirmedNavigationFields = new Set<string>();
  private confirmationDialog: MatDialogRef<EzDialogComponent> | null = null;
  readonly isConfirmingReview = signal(false);
  readonly riskReviewRequired = signal(false);
  private readonly autosaveFailed = signal(false);
  private loadRevision = 0;
  private readonly recordService = inject(HearingRecordService);
  private readonly cycleContext = inject(HearingCycleContextStore, { optional: true });
  readonly hearingText = createTerminologyUsageSignals(PHMS_HEARING_TERMINOLOGY.preHearing);
  readonly documentText = createTerminologyUsageSignals(PHMS_DOCUMENT_TERMINOLOGY.preHearing);

  /** The hosting proceeding. Setting it (re)loads the S1 record and the current S2 capture. */
  @Input({ required: true })
  set scheduledProceedingId(value: number | null) {
    if (value !== this._proceedingId()) {
      this.loadRevision++;
      this.confirmationDialog?.close(false);
      this.confirmationDialog = null;
      this.isConfirmingReview.set(false);
      this.isSaving.set(false);
      this.saveError.set(null);
      this.autosaveFailed.set(false);
      this.acknowledgementUnconfirmed = false;
      this.unconfirmedNavigationFields.clear();
      this.navigationMessage.set("");
      this.riskReviewRequired.set(false);
      this.lastSavedAt.set(null);
      this._record.set(null);
      this.applyCapture(null);
    }
    this._proceedingId.set(value);
    if (value !== null && value > 0) {
      this.load(value);
    } else this.isLoading.set(false);
  }
  get scheduledProceedingId(): number | null {
    return this._proceedingId();
  }

  /** Tells the routed Hearing Cycle host to continue into At-Hearing after persistence succeeds. */
  @Output() readonly reviewCompleted = new EventEmitter<void>();

  private readonly _proceedingId = signal<number | null>(null);
  private readonly _record = signal<HearingRecordDto | null>(null);

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly pendingSaveCount = signal(0);
  readonly saveStateLabel = computed(() => this.saveError() ? "Couldn't save"
    : this.isSaving() || this.pendingSaveCount() > 0 ? "Saving..."
      : this.lastSavedAt() ? "Saved" : "No unsaved changes");
  readonly saveError = signal<string | null>(null);
  readonly navigationMessage = signal("");
  readonly lastSavedAt = signal<Date | null>(null);
  readonly captureStatus = signal("draft");
  readonly activeTaskIndex = signal(0);
  readonly taskCompletion = signal<Record<string, string | undefined>>({});
  readonly participantBusy = signal(false);
  readonly activeCompletionKey = computed(() => this.activeTaskIndex() === 1 ? "participantReviewComplete"
    : this.activeTaskIndex() === 2 ? "privateNotesComplete" : null);
  readonly isTaskComplete = computed(() => this.taskCompletion()[this.activeCompletionKey() ?? ""] === "true");
  readonly completionBlocked = computed(() => !this.canEdit() || this._record()?.scheduledProceedingId !== this._proceedingId() || this.isLoading() || this.isSaving()
    || this.pendingSaveCount() > 0 || this.autosaveFailed() || this.activeTaskIndex() === 1 && this.participantBusy());

  markTaskComplete(): void {
    if (this.workflowManaged) return;
    const id = this._proceedingId(), key = this.activeCompletionKey();
    if (!id || !key || this.completionBlocked() || this.isTaskComplete()) return;
    const revision = this.loadRevision;
    this.isSaving.set(true); this.saveError.set(null);
    // Task acknowledgment does not change requirement statuses, delivery records, or the phase status.
    this.recordService.upsertCapture(id, { narratives: { [key]: "true" } })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: capture => {
          if (!this.isCurrentCapture(id, revision)) return;
          this.isSaving.set(false);
          if (capture.scheduledProceedingId !== id) { this.acknowledgementUnconfirmed = true; this.saveError.set("The hearing acknowledgement could not be confirmed."); return; }
          this.acknowledgementUnconfirmed = false;
          this.lastSavedAt.set(new Date());
          this.taskCompletion.set(parseNarratives(capture.narrativesJson));
          this.cycleContext?.updateCapture(capture);
        },
        error: error => {
          if (!this.isCurrentCapture(id, revision)) return;
          this.acknowledgementUnconfirmed = true;
          this.isSaving.set(false); this.saveError.set(messageFromError(error, this.hearingText.savePermissionDenied()));
        },
      });
  }
  readonly showAutosave = computed(() => this.canEdit()
    && !["Participant Notification"].includes(String(this.sectionTabs()[this.activeTaskIndex()]?.label)));
  private readonly reviewStateVersion = signal(0);

  /** Author permission gate; the form is read-only (autosave suppressed) when this is false. */
  readonly canAuthor = this.roles.hasAnyPermissionSignal(CAPTURE_AUTHOR_PERMISSION);

  /** True while the form is editable: actor can author and the proceeding is resolved. */
  readonly canEdit = computed(() => this.canAuthor() && !this.isLoading() && (this._proceedingId() ?? 0) > 0
    && this._record()?.scheduledProceedingId === this._proceedingId());

  /** Read-only context pulled from the S1 record's demographics block. */
  readonly subjectName = computed(() => {
    const demo = this._record()?.context?.demographics;
    if (!demo) {
      return "";
    }
    const full = (demo.fullName ?? "").trim();
    if (full) {
      return full;
    }
    return [demo.firstName, demo.lastName]
      .map((part) => (part ?? "").trim())
      .filter((part) => part.length > 0)
      .join(" ");
  });
  readonly custodyLabel = computed(() => {
    const demo = this._record()?.context?.demographics;
    const status = (demo?.custodyStatus ?? "").trim();
    const level = (demo?.custodyLevel ?? "").trim();
    return [status, level].filter((part) => part.length > 0).join(" · ");
  });
  readonly hasContext = computed(() => this.subjectName().length > 0 || this.custodyLabel().length > 0);

  /** Person id for the shared Case Snapshot rail (#1458 uniformity with at-hearing/inmate detail). */
  readonly incarceratedPersonId = computed(() => this._record()?.incarceratedPersonId ?? null);

  /** Renders the subject as "Last, First Middle" (#1436); subjectName() stays raw. */
  readonly formatPersonName = formatPersonNameLastFirst;

  /** Two-way bound form model for preparation tasks. */
  formValues: PreHearingFormValues = createEmptyFormValues();

  readonly releasePlanConfig = computed<InputConfig[]>(() => [
    {
      name: "releasePlanSummary",
      type: EzFormInputType.TEXTAREA,
      label: "Release plan summary",
      row: 1,
      width: "fill",
      textAreaRows: 8,
      placeholder: "Summarize housing, employment, treatment, transportation, and support plans.",
    },
  ]);

  readonly outstandingRequirements = computed(() =>
    (this._record()?.context.proceeding?.requirements ?? []).filter(requirement => !isComplete(requirement.statusCode)));
  readonly otherOutstandingRequirements = computed(() =>
    this.outstandingRequirements().filter(requirement => !isParticipantNotificationRequirement(requirement)));

  readonly followUpCount = computed(() => {
    this.reviewStateVersion();
    return this.otherOutstandingRequirements().length
      + Number(!this.formValues.releasePlanSummary.trim());
  });

  readonly isReviewComplete = computed(() => isComplete(this.captureStatus()));

  /** Autosave pipeline: each field change debounces, then upserts the changed field to the S2 capture. */
  private readonly saveRequests = new Subject<PendingCaptureEdit>();

  readonly pageTitle = this.hearingText.title;

  constructor() {
    this.destroyRef.onDestroy(() => this.confirmationDialog?.close(false));
    effect(() => { if (!this.navigationBusy()) this.navigationMessage.set(""); });
    const settledChanges = this.saveRequests.pipe(debounceTime(AUTOSAVE_DEBOUNCE_MS));
    this.saveRequests
      .pipe(
        buffer(settledChanges),
        filter(requests => requests.length > 0),
        // Preserve event order and merge only contiguous edits to the same explicit hearing/view.
        // Route reuse must never retarget old buffered input to the newly selected proceeding.
        concatMap(requests => {
          const batches: PendingCaptureBatch[] = [];
          for (const request of requests) {
            const previous = batches.at(-1);
            if (previous?.proceedingId === request.proceedingId && previous.revision === request.revision) {
              previous.payload = mergeCapturePayload(previous.payload, request.payload);
              previous.count++;
            } else batches.push({ ...request, count: 1 });
          }
          return batches;
        }),
        concatMap(({ proceedingId, revision, payload, count }) => {
          const navigationFieldKeys = capturePayloadFieldKeys(payload);
          if (this.isCurrentCapture(proceedingId, revision)) {
            this.isSaving.set(true);
            this.saveError.set(null);
          }
          // Enqueue checked local edit access; the original endpoint rechecks current server
          // authority when the queued write executes, even after this view has changed.
          return this.recordService.upsertCapture(proceedingId, payload).pipe(
            map(capture => ({ proceedingId, revision, capture, navigationFieldKeys })),
            finalize(() => {
              this.pendingSaveCount.update(value => value - count);
              if (this.isCurrentCapture(proceedingId, revision)) this.isSaving.set(false);
            }),
            catchError((error: unknown) => {
              if (this.isCurrentCapture(proceedingId, revision)) {
                this.autosaveFailed.set(true);
                for (const key of navigationFieldKeys) this.unconfirmedNavigationFields.add(key);
                this.saveError.set(messageFromError(error, this.hearingText.savePermissionDenied()));
              }
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result && this.isCurrentCapture(result.proceedingId, result.revision)) {
          if (result.capture.scheduledProceedingId !== result.proceedingId) {
            this.autosaveFailed.set(true);
            for (const key of result.navigationFieldKeys) this.unconfirmedNavigationFields.add(key);
            this.saveError.set("The hearing save could not be confirmed.");
            return;
          }
          this.autosaveFailed.set(false);
          for (const key of result.navigationFieldKeys) this.unconfirmedNavigationFields.delete(key);
          this.lastSavedAt.set(new Date());
          this.captureStatus.set(result.capture.statusCode);
          this.taskCompletion.set(parseNarratives(result.capture.narrativesJson));
          this.cycleContext?.updateCapture(result.capture);
        }
      });
  }

  private isCurrentCapture(proceedingId: number, revision: number): boolean {
    return proceedingId === this._proceedingId() && revision === this.loadRevision;
  }

  private navigationBusy(): boolean {
    return this.pendingSaveCount() > 0 || this.isSaving() || this.isConfirmingReview() || this.participantBusy();
  }

  confirmNavigation(): boolean | Promise<boolean> {
    if (this.navigationBusy()) {
      this.navigationMessage.set("Wait for record changes to finish saving, then try again.");
      return false;
    }
    this.navigationMessage.set("");
    if (this.unconfirmedNavigationFields.size === 0 && !this.acknowledgementUnconfirmed) return true;
    if (this.navigationConfirmation) return this.navigationConfirmation;
    const proceedingId = this._proceedingId(), revision = this.loadRevision;
    const pending = this.draftConfirmation.confirm({
      heading: "Leave source record?",
      message: "Some changes to this record were not confirmed as saved. Leaving will discard unsaved input.",
      confirmText: "Leave without saving", cancelText: "Stay on page",
    }).then(confirmed => {
      if (!confirmed || proceedingId !== this._proceedingId() || revision !== this.loadRevision) return false;
      if (this.navigationBusy()) {
        this.navigationMessage.set("Wait for record changes to finish saving, then try again.");
        return false;
      }
      return true;
    }).catch(() => false).finally(() => {
      if (this.navigationConfirmation === pending) this.navigationConfirmation = null;
    });
    this.navigationConfirmation = pending;
    return pending;
  }

  @HostListener("window:beforeunload", ["$event"])
  protectUnsavedInput(event: BeforeUnloadEvent): void {
    if (this.navigationBusy() || this.unconfirmedNavigationFields.size > 0 || this.acknowledgementUnconfirmed) {
      event.preventDefault();
      event.returnValue = "";
    }
  }

  /**
   * Single-field change handler wired to ez-form's `(inputChange)`. Keeps editable task values in
   * sync and enqueues a partial upsert carrying only the changed field, so autosave never overwrites
   * an untouched field with null on the partial-upsert contract.
   */
  onInputChange(event: InputChangeEvent): void {
    const proceedingId = this._proceedingId();
    if (!proceedingId || !this.canEdit()) return;
    if (event.name === "riskCraNote" && String(event.value ?? "").trim()) this.riskReviewRequired.set(false);
    const payload = this.toUpsertPayload(event.name, event.value);
    if (!payload) return;
    if (event.name in this.formValues) {
      this.formValues[event.name] = typeof event.value === "boolean"
        ? event.value
        : toStringOrEmpty(event.value);
      this.reviewStateVersion.update((version) => version + 1);
    }
    this.pendingSaveCount.update(value => value + 1);
    this.saveRequests.next({ proceedingId, revision: this.loadRevision, payload });
  }

  /** Maps a single changed field to a minimal partial-upsert payload (typed column or narrative key). */
  private toUpsertPayload(name: string, value: unknown): ProceedingHearingCaptureUpsertRequest | null {
    switch (name) {
      case "riskCraNote":
      case "institutionalBehaviorSummary":
      case "criminalHistoryNote":
      case "releasePlanSummary":
        return { narratives: { [name]: toStringOrEmpty(value) } };
      case "privateNotes":
        // privateNotes is the Private Notes tab (#1458 SDMF requirements): persisted as a capture
        // narrative through the same debounced autosave. The S2 capture is proceeding-scoped, not
        // per-user — until a per-user notes store exists, these notes are private in presentation,
        // not in storage.
        return { narratives: { [name]: toStringOrEmpty(value), privateNotesComplete: "false" } };
      default:
        return null;
    }
  }

  private load(scheduledProceedingId: number): void {
    const revision = ++this.loadRevision;
    this.isLoading.set(true);
    this.autosaveFailed.set(false);
    this.unconfirmedNavigationFields.clear();
    this.riskReviewRequired.set(false);
    this.saveError.set(null);

    (this.cycleContext?.load(scheduledProceedingId) ?? this.recordService.getRecord(scheduledProceedingId))
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((record) => {
        if (!this.isCurrentCapture(scheduledProceedingId, revision)) return;
        this.isLoading.set(false);
        if (record && (record.scheduledProceedingId !== scheduledProceedingId
          || record.captured?.capture && record.captured.capture.scheduledProceedingId !== scheduledProceedingId)) {
          this._record.set(null);
          this.applyCapture(null);
          this.saveError.set("The hearing record could not be confirmed. Reload before editing.");
          return;
        }
        this._record.set(record);
        this.applyCapture(record?.captured?.capture ?? null);
      });

  }

  /**
   * Completes the pre-hearing review without treating advisory follow-ups as hard blockers.
   * Permission, in-flight save, and already-complete safeguards remain enforced by the surface.
   */
  markReviewComplete(): void {
    if (this.workflowManaged) return;
    const proceedingId = this._proceedingId();
    const revision = this.loadRevision;
    if (!proceedingId || !this.canEdit() || this.isLoading() || this.isSaving() || this.pendingSaveCount() > 0 || this.isReviewComplete() || this.isConfirmingReview()) {
      return;
    }
    if (!String(this.formValues.riskCraNote ?? "").trim()) {
      this.riskReviewRequired.set(true);
      this.saveError.set(this.recordOnlyReview
        ? "Risk assessment review is required before saving the review acknowledgement."
        : "Risk assessment review is required before completing the review.");
      return;
    }
    if (this.autosaveFailed()) return;
    this.saveError.set(null);
    this.riskReviewRequired.set(false);
    const actor = this.roles.accessProfile()?.actor;
    const name = [actor?.displayName, this.user.userFriendlyName(), actor?.username, actor?.email]
      .map(value => value?.trim()).find(Boolean) ?? "the signed-in user";
    // EzDialog renders question text as HTML; the account name must remain plain text.
    const escapedName = name.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    const data: EzDialogData = {
      heading: this.recordOnlyReview ? "Save review acknowledgement" : this.hearingText.reviewConfirmHeading(),
      headerStyle: "info",
      question: this.recordOnlyReview
        ? `I, ${escapedName}, acknowledge that I reviewed the criminal and institutional history and recorded the risk assessment review. This saves an acknowledgement to this hearing record only; it does not complete or advance a workflow.`
        : `I, ${escapedName}, ${this.hearingText.reviewConfirmStatement()}`,
      buttons: [
        { text: "Cancel", action: ref => ref.close(false) },
        { text: this.recordOnlyReview ? "Save acknowledgement" : this.hearingText.reviewConfirmButton(), action: ref => ref.close(true) },
      ],
    };
    this.isConfirmingReview.set(true);
    this.confirmationDialog = this.dialog.open(EzDialogComponent, {
      width: "38rem", maxWidth: "calc(100vw - 2rem)", autoFocus: "first-tabbable",
      restoreFocus: true, ariaLabel: data.heading, data,
    });
    this.confirmationDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (!this.isCurrentCapture(proceedingId, revision)) return;
      this.confirmationDialog = null;
      this.isConfirmingReview.set(false);
      if (confirmed === true && this.isCurrentCapture(proceedingId, revision)) this.completeConfirmedReview(proceedingId, revision);
    });
  }

  private completeConfirmedReview(proceedingId: number, revision: number): void {
    if (this.workflowManaged || !this.isCurrentCapture(proceedingId, revision)) return;
    if (!this.canEdit() || this.isLoading() || this.isSaving() || this.pendingSaveCount() > 0
      || this.isReviewComplete() || this.autosaveFailed() || !String(this.formValues.riskCraNote ?? "").trim()) return;

    this.isSaving.set(true);
    this.saveError.set(null);
    this.recordService
      .upsertCapture(proceedingId, {
        statusCode: "complete",
        narratives: {
          criminalHistoryConfirmed: "true",
          riskCraNote: this.formValues.riskCraNote.trim(),
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (capture) => {
          if (!this.isCurrentCapture(proceedingId, revision)) return;
          this.isSaving.set(false);
          if (capture.scheduledProceedingId !== proceedingId) { this.acknowledgementUnconfirmed = true; this.saveError.set("The hearing acknowledgement could not be confirmed."); return; }
          this.acknowledgementUnconfirmed = false;
          this.lastSavedAt.set(new Date());
          this.captureStatus.set(capture.statusCode);
          this.cycleContext?.updateCapture(capture);
          if (isComplete(capture.statusCode)) {
            this.formValues.criminalHistoryConfirmed = true;
            this.reviewStateVersion.update(version => version + 1);
            if (!this.recordOnlyReview) this.reviewCompleted.emit();
          }
        },
        error: (error: unknown) => {
          if (!this.isCurrentCapture(proceedingId, revision)) return;
          this.acknowledgementUnconfirmed = true;
          this.isSaving.set(false);
          this.saveError.set(messageFromError(error, this.hearingText.savePermissionDenied()));
        },
      });
  }

  /**
   * Bumped whenever {@link formValues} is REASSIGNED (load/hydrate), so the tab config rebuilds and
   * the Eligibility tab re-receives the fresh model reference. Keystroke edits mutate in place via
   * events and never bump this.
   */
  private readonly formModelVersion = signal(0);

  /**
   * Private Notes model (#1458 SDMF requirements): a single rich-text field (the library's
   * ckEditor-backed RICH_TEXT form input — the "basic editor" component) persisted as the
   * `privateNotes` capture narrative through the shared autosave pipeline.
   */
  privateNotesValues: Record<string, unknown> = { privateNotes: "" };

  readonly privateNotesConfig = computed<InputConfig[]>(() => [
    {
      name: "privateNotes",
      type: EzFormInputType.RICH_TEXT,
      label: "Working notes",
      editorConfig: {
        toolbar: { items: ["undo", "redo", "|", "bold", "italic", "underline", "bulletedList", "numberedList", "outdent", "indent"], shouldNotGroupWhenFull: true },
        placeholder: this.hearingText.notesPlaceholder(),
      },
      editorFont: "inherit",
      placeholder: this.hearingText.notesPlaceholder(),
      // Full-width editor: without this the field sits in a default form column and the
      // rich-text surface renders needlessly narrow.
      row: 1,
      width: "fill",
    },
  ]);

  /** Private notes are author-only; SDMF has its own shared hearing destination. */
  private readonly readOnlyViewer = computed(() => !this.canAuthor());

  readonly sectionTabs = computed<TabConfig[]>(() => {
    this.formModelVersion();
    const readOnly = this.readOnlyViewer;
    // Explicitly typed so each literal is contextually a TabConfig (mixed event maps otherwise
    // union-infer optional-undefined members that fail the events index signature).
    const tabs: TabConfig[] = [
      {
        label: "Case Review",
        component: PreHearingCaseReviewSectionComponent,
        inputs: {
          record: this._record,
          canEdit: this.canEdit,
          riskValues: this.formValues,
          riskReviewRequired: this.riskReviewRequired(),
          releasePlanConfig: this.releasePlanConfig(),
        },
        events: {
          inputChange: (event: unknown) => this.onInputChange(event as InputChangeEvent),
          riskValuesChange: (values: unknown) => {
            Object.assign(this.formValues, values as Partial<PreHearingFormValues>);
            this.reviewStateVersion.update((version) => version + 1);
          },
        },
      },
      {
        label: "Participant Notification",
        component: PreHearingVictimNotificationSectionComponent,
        inputs: { record: this._record },
        events: { busyChanged: (busy: unknown) => this.participantBusy.set(busy === true) },
      },
      {
        label: "Private Notes",
        component: PreHearingAnalysisNotesSectionComponent,
        hidden: readOnly,
        inputs: {
          notesConfig: this.privateNotesConfig(),
          notesHelp: this.hearingText.notesPlaceholder(),
          notesValues: this.privateNotesValues,
          canEdit: this.canEdit,
        },
        events: {
          inputChange: (event: unknown) => this.onInputChange(event as InputChangeEvent),
          notesValuesChange: (values: unknown) => {
            Object.assign(this.privateNotesValues, values as Record<string, unknown>);
          },
        },
      },
      {
        label: this.documentText.documents(),
        // The SAME person-scoped Case Documents surface the inmate-detail Documents tab renders
        // (upload, packets, statuses) — not the proceeding-scoped management screen — so the two
        // pages always show the same document set.
        component: PhmsCaseDocumentsComponent,
        inputs: {
          incarceratedPersonId: this.incarceratedPersonId(),
          subjectName: this.subjectName(),
        },
      },
    ];
    return tabs;
  });

  /** Hydrate the form model from an existing capture (S2). */
  private applyCapture(capture: ProceedingHearingCaptureDto | null): void {
    this.taskCompletion.set(parseNarratives(capture?.narrativesJson));
    if (!capture) {
      this.formValues = createEmptyFormValues();
      this.privateNotesValues = { privateNotes: "" };
      this.captureStatus.set("draft");
      this.reviewStateVersion.update((version) => version + 1);
      this.formModelVersion.update((version) => version + 1);
      return;
    }
    const narratives = parseNarratives(capture.narrativesJson);
    const confirmed = narratives["criminalHistoryConfirmed"] === "true";
    this.privateNotesValues = {
      privateNotes: narratives["privateNotes"] ?? "",
    };
    this.formValues = {
      eligibilityTrack: capture.eligibilityTrack ?? "",
      citizenshipStatus: capture.citizenshipStatus ?? "",
      riskCraNote: narratives["riskCraNote"] ?? "",
      criminalHistoryConfirmed: confirmed,
      criminalHistoryNote: narratives["criminalHistoryNote"] ?? "",
      institutionalBehaviorSummary: narratives["institutionalBehaviorSummary"] ?? "",
      stakeholderInputSummary: narratives["stakeholderInputSummary"] ?? "",
      releasePlanSummary: narratives["releasePlanSummary"] ?? "",
    };
    this.captureStatus.set(capture.statusCode);
    this.reviewStateVersion.update((version) => version + 1);
    this.formModelVersion.update((version) => version + 1);
  }
}

function capturePayloadFieldKeys(payload: ProceedingHearingCaptureUpsertRequest): string[] {
  return [
    ...Object.keys(payload).filter(key => key !== "narratives").map(key => `field:${key}`),
    ...Object.keys(payload.narratives ?? {}).map(key => `narrative:${key}`),
  ];
}

function mergeCapturePayload(merged: ProceedingHearingCaptureUpsertRequest, request: ProceedingHearingCaptureUpsertRequest): ProceedingHearingCaptureUpsertRequest {
  return { ...merged, ...request,
    ...(merged.narratives || request.narratives ? { narratives: { ...merged.narratives, ...request.narratives } } : {}) };
}

function createEmptyFormValues(): PreHearingFormValues {
  return {
    eligibilityTrack: "",
    citizenshipStatus: "",
    riskCraNote: "",
    criminalHistoryConfirmed: false,
    criminalHistoryNote: "",
    institutionalBehaviorSummary: "",
    stakeholderInputSummary: "",
    releasePlanSummary: "",
  };
}

function parseNarratives(json: string | null | undefined): Record<string, string> {
  if (!json) {
    return {};
  }
  try {
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === "object") {
      const out: Record<string, string> = {};
      for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
        out[key] = value == null ? "" : String(value);
      }
      return out;
    }
  } catch {
    // Malformed narratives blob — treat as empty rather than failing the surface.
  }
  return {};
}

function toStringOrEmpty(value: unknown): string {
  if (value == null) {
    return "";
  }
  return String(value).trim();
}

function isComplete(value: string | null | undefined): boolean {
  const normalized = (value ?? "").trim().toLowerCase();
  return normalized === "complete" || normalized === "completed" || normalized === "satisfied" || normalized === "final";
}

function messageFromError(error: unknown, permissionDeniedMessage: string): string {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status?: unknown }).status;
    if (status === 403) {
      return permissionDeniedMessage;
    }
  }
  return "Autosave failed. Your last change was not saved.";
}
